'use client';
import { useFormatter, useTranslations } from 'next-intl';
import type { FC } from 'react';
import { v4 } from 'uuid';
import { BaseLineMusic } from '@/app/components/icons/BaseLineMusic';
import { BaseLineRecreate } from '@/app/components/icons/BaseLineRecreate';
import type { ActionDialogProps } from '@/app/components/shared/ActionDialog';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Button } from '@/app/components/ui/button';
import { TYPE_KEY_MAP } from '@/app/constants';
import { useDevice, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { CreationInfo, CreationWithTask, TaskInfo } from '@/app/types';
import { cn, renderT2APrompt, reportEvent } from '@/app/utils';
import { convertTTSPromptsToTextPrompt } from '@/app/utils/client';
import { useTaskCard } from '../hooks/use-task-card';
import { getAudioButtonConfig } from '../utils/button-config';
import { AudioCreationCard } from './AudioCreationCard';
import { CancelButton } from './CancelButton';
import { CreationActionButtons } from './CreationActionButtons';

interface AudioTaskCardProps {
  task: TaskInfo;
  onOpenDetail: (creationId: CreationWithTask) => void;
  scrollToTop?: () => void;
}

export const AudioTaskCard: FC<AudioTaskCardProps> = ({ task, onOpenDetail, scrollToTop }) => {
  const t = useTranslations();
  const { updateCreation } = useStore((state) => ({
    updateCreation: state.updateCreation,
  }));
  const { open: openActionDialog } = useGlobalModal('actionDialog');
  const { open: openPostsModal } = useGlobalModal('postsModal');
  const { isMobile } = useDevice();

  const {
    taskState,
    hasFinished,
    canCancelTask,
    queueWaitTime,
    isWaitTimeProlonged,
    handleCancelTask,
    handleSelectedAction,
    handleRecreateAction,
  } = useTaskCard({ task, scrollToTop });

  const taskHistoryContainer = document.getElementById('taskHistoryContainer');
  const { input, id: taskId, created_at } = task;
  const creations = task.creations ?? [];
  const audioPrompts = input.prompts?.filter((p) => p.type === 'text') || [];

  const format = useFormatter();
  const date = new Date(created_at);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // 投稿处理
  const handlePosts = (creation: CreationInfo) => {
    openPostsModal({
      creation: { ...creation, task: task },
      mobileNextStep: isMobile,
      onClose: (is_asset) => updateCreation(taskId, creation.id, { is_asset }),
    });
    reportEvent('submit_click', {
      submit_position: 'tools-submit',
    });
  };

  // 处理对话框操作
  const handleOpenActionDialog = (dialogAction: ActionDialogProps) => {
    openActionDialog(dialogAction);
  };

  // 获取要显示的操作按钮列表
  const showActionList = getAudioButtonConfig({
    creation: creations[0],
    task,
  });
  const isTtsTask = (task.type as string) === 'tts';
  const audioPromptContents = isTtsTask
    ? [convertTTSPromptsToTextPrompt(input.prompts)].filter(Boolean)
    : audioPrompts.map((prompt) => renderT2APrompt(prompt));

  return (
    <div className="sm:rounded-12 sm:bg-system-bg04 sm:p-3" key={taskId}>
      {/* 音频任务标题 */}
      <div className="flex items-center sm:hidden">
        <div className="size-7 rounded-full bg-system-white10 p-1">
          <BaseLineMusic className="size-5" />
        </div>
        <p className="ml-3 text-sm text-system-white opacity-90">{t(`create.${TYPE_KEY_MAP[task.type]}`)}</p>
        <div className="ml-2 text-sm text-system-white36">
          {format.dateTime(date, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZone,
          })}
        </div>
      </div>

      {/* 音频提示词 */}
      <div
        className={cn(
          'mt-2 mb-3 ml-10 pr-3 text-sm text-system-white48 md:my-3 md:ml-0',
          'h-auto max-w-[600px] lg:h-auto',
        )}
      >
        <div className="flex h-auto flex-1 items-center lg:h-auto">
          <div className="flex h-full w-full flex-1 items-center">
            {!!audioPrompts.length && (
              <CustomTooltip
                collisionBoundary={taskHistoryContainer}
                contentClassName="overflow-y-auto max-h-[400px] [word-break:break-word]"
                collisionTop={52}
                showOnlyOnOverflow={true}
                maxLines={2}
                content={audioPromptContents.map((promptText) => (
                  <p key={v4()} className="whitespace-normal break-all">
                    {promptText}
                  </p>
                ))}
                sideOffset={8}
                trigger="auto"
              >
                {audioPromptContents.map((promptText) => (
                  <span key={v4()} className="mr-2">
                    {promptText}
                  </span>
                ))}
              </CustomTooltip>
            )}
          </div>
        </div>
      </div>

      <div className={cn('ml-10 md:mr-0 md:ml-0 md:max-w-full lg:mr-10', 'max-w-[600px]')}>
        <AudioCreationCard
          creation={creations[0]}
          task={task}
          taskState={taskState}
          queueWaitTime={queueWaitTime}
          isWaitTimeProlonged={isWaitTimeProlonged}
          onOpenActionDialog={handleOpenActionDialog}
          onOpenDetail={onOpenDetail}
          onCancelTask={handleCancelTask}
          onSubmit={handlePosts}
        />

        <div className="mt-3 flex items-center justify-between sm:flex-row-reverse lg:mt-2">
          <div className="flex items-center gap-2">
            {/* 做同款按钮 */}
            <Button
              className="h-8 gap-2 rounded-8 bg-system-bg02 px-3 font-normal enabled:hover:bg-system-hover01 disabled:text-system-white24"
              onClick={handleRecreateAction}
              icon={<BaseLineRecreate className="text-base sm:hidden" />}
            >
              {t('task.action.reprompt')}
            </Button>
          </div>

          {/* 卡片右下角的一排按钮 */}
          {isMobile && (
            <div className="rounded-8 bg-system-bg02">
              <div
                className="flex h-full items-center aria-disabled:pointer-events-none aria-disabled:opacity-[.24] aria-disabled:sm:hidden"
                aria-disabled={!hasFinished}
              >
                <CreationActionButtons
                  creation={creations[0]}
                  task={task}
                  showActionList={showActionList}
                  onSubmit={handlePosts}
                  onSelectedAction={handleSelectedAction}
                />
              </div>
            </div>
          )}

          {/* 移动端取消按钮 */}
          {canCancelTask && (
            <CancelButton
              className="hidden bg-system-bg02 enabled:hover:bg-system-hover01 disabled:text-system-white24 sm:flex"
              taskId={taskId}
              onCancel={handleCancelTask}
            />
          )}
        </div>
      </div>
    </div>
  );
};
